import { useState, useEffect, useRef, TouchEvent } from "react";
import SajuContentSlider from "./SajuContentSlider";
import { getAdBannerById, luckTypeToAdBannerId } from "../data/adBannerData";
import { getScoreMessage } from "../data/luckMessages";

interface IThreeLuckRow {
  header: string;
  headerClassName: string;
  title: string;
  score: number | null;
  content: string;
  subTitle: string;
  scoreMessage: string;
  onTitleClick: () => void;
}

const ThreeLuckRow = ({
  header,
  headerClassName,
  title,
  score,
  content,
  subTitle,
  scoreMessage,
  onTitleClick,
}: IThreeLuckRow) => {
  return (
    <div className={`luck_card ${headerClassName}`}>
      <div className={`luck_card_header ${headerClassName}`}>
        <div className="luck_title">
          {header}
          {/* <span className="icon"></span> */}
        </div>
        <div className="luck_score">{scoreMessage}</div>
      </div>

      <div className="luck_card_body">
        {score !== null && (
          <div className={`luck_card_score ${headerClassName}`}>{score}점</div>
        )}
        <div className={`luck_card_title`}>{title}</div>
        <div className="luck_card_content">{content}</div>
      </div>

      <div className="luck_card_footer">
        <button className="luck_card_button" onClick={onTitleClick}>
          자세히 읽어보기
        </button>
      </div>
    </div>
  );
};

interface IThreeLuck {
  money_score: number | null;
  money_content: string;
  money_sub_title: string;
  money_title: string;
  love_score: number | null;
  love_content: string;
  love_sub_title: string;
  love_title: string;
  job_score: number | null;
  job_content: string;
  job_sub_title: string;
  job_title: string;
  showAds?: boolean;
}

const ThreeLuck = ({
  money_score,
  money_content,
  money_sub_title,
  money_title,
  love_score,
  love_content,
  love_sub_title,
  love_title,
  job_score,
  job_content,
  job_sub_title,
  job_title,
  showAds = true,
}: IThreeLuck) => {
  const [expandedLuck, setExpandedLuck] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>("");
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalSubTitle, setModalSubTitle] = useState<string>("");
  const [titleClass, setTitleClass] = useState<string>("");
  const [modalScore, setModalScore] = useState<number | null>(null);

  // 각 운에 대한 메시지를 저장할 상태 추가
  const [moneyScoreMessage, setMoneyScoreMessage] = useState<string>("");
  const [loveScoreMessage, setLoveScoreMessage] = useState<string>("");
  const [jobScoreMessage, setJobScoreMessage] = useState<string>("");

  // 컴포넌트가 마운트될 때 한 번만 메시지 생성
  useEffect(() => {
    setMoneyScoreMessage(getScoreMessage("money", money_score));
    setLoveScoreMessage(getScoreMessage("love", love_score));
    setJobScoreMessage(getScoreMessage("job", job_score));
  }, [money_score, love_score, job_score]);

  // 모바일 여부 확인
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => {
      window.removeEventListener("resize", checkIsMobile);
    };
  }, []);

  const handleTitleClick = (luckType: string) => {
    if (expandedLuck === luckType) {
      setExpandedLuck(null);
      document.body.style.overflow = "auto";
      return;
    }

    let content = "";
    let header = "";
    let subTitle = "";
    let className = "";
    let score = null;

    switch (luckType) {
      case "money":
        content = money_content;
        header = "재물운";
        subTitle = money_sub_title;
        className = "money";
        score = money_score;
        break;
      case "love":
        content = love_content;
        header = "연애운";
        subTitle = love_sub_title;
        className = "love";
        score = love_score;
        break;
      case "job":
        content = job_content;
        header = "직장운";
        subTitle = job_sub_title;
        className = "job";
        score = job_score;
        break;
    }

    setModalContent(content);
    setModalTitle(header);
    setModalSubTitle(subTitle);
    setTitleClass(className);
    setModalScore(score);
    setExpandedLuck(luckType);

    // 모달이 열렸을 때 배경 스크롤 방지
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setExpandedLuck(null);
    document.body.style.overflow = "auto";
  };

  // 현재 선택된 운세에 맞는 광고 배너 가져오기
  const getCurrentAdBanner = () => {
    if (!showAds) return null;
    if (!expandedLuck) return null;

    // 운세 유형에 맞는 배너 ID 가져오기
    const bannerId = luckTypeToAdBannerId[expandedLuck];
    if (!bannerId) return null;

    // 해당 ID의 배너 데이터 가져오기
    return getAdBannerById(bannerId);
  };

  // 현재 선택된 배너 가져오기
  const currentBanner = getCurrentAdBanner();

  const bottomSheetRef = useRef<HTMLDivElement>(null);
  const [startY, setStartY] = useState<number>(0);
  const [currentY, setCurrentY] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  // 터치 이벤트 핸들러
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY;

    if (deltaY > 0) {
      // 아래로 드래그할 때만 이동
      setCurrentY(deltaY);
      if (bottomSheetRef.current) {
        bottomSheetRef.current.style.transform = `translateY(${deltaY}px)`;
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (currentY > 100) {
      // 100px 이상 드래그하면 닫기
      closeModal();
    } else {
      // 원위치로 돌아가기
      if (bottomSheetRef.current) {
        bottomSheetRef.current.style.transform = "translateY(0)";
      }
    }
    setCurrentY(0);
  };

  // 모달이 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (expandedLuck && isMobile) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "auto";
      document.body.style.position = "static";
      document.body.style.width = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
      document.body.style.position = "static";
      document.body.style.width = "auto";
    };
  }, [expandedLuck, isMobile]);

  return (
    <div className="luck_cards_container">
      <ThreeLuckRow
        header="재물운"
        headerClassName="money"
        title={money_title}
        score={money_score}
        content={money_content}
        subTitle={money_sub_title}
        scoreMessage={moneyScoreMessage}
        onTitleClick={() => handleTitleClick("money")}
      />
      <ThreeLuckRow
        header="연애운"
        headerClassName="love"
        title={love_title}
        score={love_score}
        content={love_content}
        subTitle={love_sub_title}
        scoreMessage={loveScoreMessage}
        onTitleClick={() => handleTitleClick("love")}
      />
      <ThreeLuckRow
        header="직장운"
        headerClassName="job"
        title={job_title}
        score={job_score}
        content={job_content}
        subTitle={job_sub_title}
        scoreMessage={jobScoreMessage}
        onTitleClick={() => handleTitleClick("job")}
      />

      {/* 데스크톱 모달 */}
      {expandedLuck && !isMobile && (
        <div className="modal_backdrop" onClick={closeModal}>
          <div className="modal_container" onClick={(e) => e.stopPropagation()}>
            <div className={`modal_header ${titleClass}`}>
              <div className="modal_title">{modalTitle}</div>
              <div className="modal_close" onClick={closeModal}>
                ×
              </div>
            </div>
            <div className="modal_content">
              {/* 모달 내 점수 바 */}
              <div className="circular-progress-container">
                <div
                  className={`circular-progress ${titleClass}`}
                  style={
                    { "--progress": `${modalScore}%` } as React.CSSProperties
                  }
                >
                  <div className="progress-score">
                    {modalScore}
                    <span className="progress-score-unit">점</span>
                  </div>
                </div>
              </div>

              {/* 모달 내 부제목 표시 */}
              <div className={`modal_sub_title ${titleClass}`}>
                {modalSubTitle}
              </div>

              <div className="modal_text_content">{modalContent}</div>

              {currentBanner && (
                <div className="modal_ad_banner">
                  <div className="ad_banner_title">
                    👇 이런 날엔 이거 꼭 읽어보세요
                  </div>
                  <SajuContentSlider {...currentBanner} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 모바일 바텀시트 */}
      {expandedLuck && isMobile && (
        <div className="bottom_sheet_backdrop" onClick={closeModal}>
          <div
            ref={bottomSheetRef}
            className="bottom_sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`bottom_sheet_header ${titleClass}`}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="bottom_sheet_handle"></div>
              <div className="bottom_sheet_header_content">
                <div className="modal_title">{modalTitle}</div>
                <div className="modal_close" onClick={closeModal}>
                  ×
                </div>
              </div>
            </div>
            <div className="bottom_sheet_content">
              {/* 모달 내 점수 바 */}
              <div className="circular-progress-container">
                <div
                  className={`circular-progress ${titleClass}`}
                  style={
                    { "--progress": `${modalScore}%` } as React.CSSProperties
                  }
                >
                  <div className="progress-score">
                    {modalScore}
                    <span className="progress-score-unit">점</span>
                  </div>
                </div>
              </div>

              {/* 모달 내 부제목 표시 */}
              <div className={`modal_sub_title ${titleClass}`}>
                {modalSubTitle}
              </div>

              <div className="modal_text_content">{modalContent}</div>

              {currentBanner && (
                <div className="modal_ad_banner">
                  <div className="ad_banner_title">
                    👇 이런 날엔 이거 꼭 읽어보세요
                  </div>
                  <SajuContentSlider {...currentBanner} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThreeLuck;
